// Telemetry event kinds — single source of truth for client + server emit.
// Client kinds cover authenticated-only SPA interactions; server kinds mirror
// the existing services/analytics.emit() transitional events so the admin
// surface has one filter list across both sources.
//
// Adding a kind: append to TELEMETRY_KINDS, then add a TELEMETRY_DISPLAY entry
// in ./display.ts (the satisfies clause turns missing entries into a compile
// error). The Zod batch limit (50) matches the client-library flush threshold.

import { z } from 'zod';

export const TELEMETRY_KINDS = [
  // ---- app lifecycle (client) ----
  'session.started',
  'route.changed',
  'web_vitals.reported',
  'error.client',
  'error.boundary',
  'error.api',

  // ---- auth (client, post-session) ----
  'auth.hat_switched',
  'auth.signed_out',
  'auth.password_changed',

  // ---- browse (client) ----
  'browse.filter_applied',
  'browse.filter_zero_results',
  'browse.map_view_toggled',
  'browse.map_bounds_changed',
  'browse.card_clicked',
  'browse.load_more_clicked',
  'browse.smart_match_toggled',

  // ---- detail pages (client) ----
  'venue.detail_viewed',
  'venue.send_request_clicked',
  'artist.detail_viewed',
  'event.detail_viewed',

  // ---- events flow (client) ----
  'event.interest_modal_opened',
  'event.interest_modal_closed',
  'event.interest_submitted',
  'event.interest_withdrawn',

  // ---- request modal + thread (client) ----
  'request.modal_opened',
  'request.modal_closed',
  'request.submitted',
  'inbox.thread_clicked',
  'inbox.filter_chip_clicked',
  'inbox.search_used',
  'thread.message_sent',
  'thread.message_send_failed',
  'thread.attachment_uploaded',
  'thread.attachment_upload_failed',

  // ---- dashboard (client) ----
  'dashboard.viewed',
  'dashboard.stat_clicked',
  'dashboard.up_next_action_clicked',
  'dashboard.quick_action_clicked',

  // ---- forms (client, generic via useFormTelemetry) ----
  'form.started',
  'form.field_validation_error',
  'form.abandoned',
  'form.submitted',
  'form.submit_failed',

  // ---- friction (client) ----
  'empty_state.viewed',
  'empty_state.cta_clicked',

  // ---- host-specific (client) ----
  'host.address_lookup_used',

  // ---- share (client) ----
  'share.exchange_card_opened',
  'share.exchange_card_action',

  // ---- server-emitted (mirrored from services/analytics.emit) ----
  'account.signed_up',
  'account.signed_in',
  'exchange.drafted',
  'exchange.submitted',
  'exchange.confirmed',
  'exchange.declined',
  'exchange.cancelled',
  'exchange.completed',
  'event.published',
  'event_interest.submitted',
  'event_interest.accepted',
  'review.submitted',
  'review.released',
  'subscription.trial_started',
  'subscription.trial_ended_first_charge',
  'verification.submitted',
  'verification.approved',
  'share.viewed',
] as const;

export type TelemetryEventKind = (typeof TELEMETRY_KINDS)[number];

export const TELEMETRY_SURFACES = [
  'browse',
  'venue-detail',
  'event-detail',
  'inbox',
  'thread',
  'dashboard-artist',
  'dashboard-host',
  'host-venue-create',
  'host-event-create',
  'host-event-manage',
  'settings',
  'admin',
  'explore',
  'exchange-detail',
  'marketing',
  'auth',
  'other',
] as const;

export type TelemetrySurface = (typeof TELEMETRY_SURFACES)[number];

export const TELEMETRY_FORM_IDS = [
  'host.venue-create',
  'host.venue-edit',
  'host.event-create',
  'host.event-manage',
  'dashboard.profile-edit',
  'disputes.submit',
  'settings.profile',
  'settings.notifications',
  'settings.password',
  'settings.close-account',
] as const;

export type TelemetryFormId = (typeof TELEMETRY_FORM_IDS)[number];

export const TelemetryEventSchema = z.object({
  kind: z.enum(TELEMETRY_KINDS),
  ts: z.number().int(),
  sessionId: z.string().min(1).max(128),
  pagePath: z.string().max(256),
  props: z.record(z.string(), z.unknown()).optional(),
  durationMs: z.number().int().nonnegative().optional(),
  clientTsOffsetMs: z.number().int().nonnegative().optional(),
});

export type TelemetryEvent = z.infer<typeof TelemetryEventSchema>;

export const TelemetryBatchSchema = z.object({
  events: z.array(TelemetryEventSchema).min(1).max(50),
});

export type TelemetryBatch = z.infer<typeof TelemetryBatchSchema>;

export function isTelemetryEventKind(value: string): value is TelemetryEventKind {
  return (TELEMETRY_KINDS as readonly string[]).includes(value);
}

export function isTelemetrySurface(value: string): value is TelemetrySurface {
  return (TELEMETRY_SURFACES as readonly string[]).includes(value);
}

export function isTelemetryFormId(value: string): value is TelemetryFormId {
  return (TELEMETRY_FORM_IDS as readonly string[]).includes(value);
}
